import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import LandingPage from '@/pages/LandingPage';
import Wordmark from '@/components/Wordmark';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Logget ind!');
      const from = location.state?.from || '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login fejlede');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Landing page blurred behind */}
      <div className="fixed inset-0 overflow-auto blur-sm brightness-50 pointer-events-none">
        <LandingPage />
      </div>

      {/* Modal overlay */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-[var(--surface-overlay)]" onClick={() => navigate('/')} />
        <Card className="relative z-10 w-full max-w-md p-8 bg-th-sidebar border-th-border shadow-2xl shadow-brand-500/10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <a href="/" className="flex items-center gap-3">
              <Mail size={40} className="text-brand-500" />
              <span className="text-2xl font-heading font-bold text-th-text">
                <Wordmark />
              </span>
            </a>
          </div>

          <h1 className="text-2xl font-heading font-bold text-th-text text-center mb-2">
            Velkommen tilbage
          </h1>
          <p className="text-th-text-secondary text-center mb-8">
            Log ind for at fortsætte
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din@email.dk"
                required
                autoFocus
                data-testid="login-email-input"
                className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted"
              />
            </div>

            <div className="relative">
              <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="pl-10 h-12 bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-btn hover:bg-btn-hover text-btn-fg text-lg"
            >
              {loading ? 'Logger ind...' : 'Log ind'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <p className="text-th-text-secondary text-sm">
              <Link to="/forgot-password" className="text-th-text-muted hover:text-brand-400 text-sm">
                Glemt adgangskode?
              </Link>
            </p>
            <p className="text-th-text-secondary text-sm">
              Har du ikke en konto?{' '}
              <Link
                to="/register"
                className="text-brand-500 hover:text-brand-400 font-medium"
                data-testid="login-register-link"
              >
                Opret konto
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
